import axios from "axios";
import {
  FormEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Col, Dropdown } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import { useMutation, useQuery, useQueryClient } from "react-query";

import DateRangePicker from "../components/DateRangePicker";
import JournalEntriesTable from "../components/JournalEntriesTable";
import JournalEntryForm from "../components/JournalEntryForm";
import PaginationComponent from "../components/Pagination";
import { createIdempotencyKey } from "../helpers/idempotencyKey";
import {
  defaultJournalRange,
  dubaiToday,
  exceedsOneMonth,
  journalDayEndIso,
  journalDayStartIso,
  journalPostingBounds,
} from "../helpers/journalDateRange";
import {
  getJournalEntries,
  reverseJournalEntry,
} from "../services/journalEntriesServices";
import {
  JournalEntryCursor,
  JournalEntryItem,
  JournalEntryResult,
  JournalEntrySearchQuery,
  JournalEntrySortDirection,
  JournalEntrySortField,
  JournalEntryType,
  JournalGlAccount,
} from "../types/journalEntries";

import "../components/Search/index.scss";
import styles from "../styles/journalEntries.module.scss";

const PAGE_SIZE = 25;
const DATE_RANGE_ERROR = "Date range cannot exceed one month.";

const ENTRY_TYPES: { value: JournalEntryType; label: string }[] = [
  { value: "CREDIT", label: "Credit" },
  { value: "DEBIT", label: "Debit" },
];

const cursorFor = (
  item: JournalEntryResult["items"][number],
  sortBy: JournalEntrySortField
): JournalEntryCursor => ({
  id: item.id,
  sortValue: sortBy === "createdAt" ? item.createdAt : item.entryDate,
});

const messageFrom = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { errorMessage?: unknown } | undefined;
    if (typeof data?.errorMessage === "string" && data.errorMessage) {
      return data.errorMessage;
    }
  }
  return "Something went wrong. Please try again.";
};

const JournalEntries = () => {
  const queryClient = useQueryClient();
  const postingBounds = useMemo(() => journalPostingBounds(), []);
  const initialRange = useMemo(() => defaultJournalRange(), []);
  const [dateFrom, setDateFrom] = useState<Date | null>(initialRange.start);
  const [dateTo, setDateTo] = useState<Date | null>(initialRange.end);
  const [dateError, setDateError] = useState<string>();
  const [glAccountCode, setGlAccountCode] = useState<string>();
  const [entryType, setEntryType] = useState<JournalEntryType>();
  const [sortBy, setSortBy] = useState<JournalEntrySortField>("entryDate");
  const [sortDir, setSortDir] = useState<JournalEntrySortDirection>("desc");
  const [page, setPage] = useState(1);
  const [cursors, setCursors] = useState<(JournalEntryCursor | undefined)[]>([
    undefined,
  ]);
  const [glAccounts, setGlAccounts] = useState<JournalGlAccount[]>([]);
  const [creating, setCreating] = useState(false);
  const [reverseTarget, setReverseTarget] = useState<JournalEntryItem>();
  const [reverseDate, setReverseDate] = useState(dubaiToday);
  const [reverseIdempotencyKey, setReverseIdempotencyKey] =
    useState(createIdempotencyKey);
  const reverseKeyDate = useRef(reverseDate);
  const [notice, setNotice] = useState<string>();

  const reverseMutation = useMutation(reverseJournalEntry, {
    onSuccess: () => {
      setReverseTarget(undefined);
      setNotice("Journal reversed.");
      const key = createIdempotencyKey();
      reverseKeyDate.current = dubaiToday();
      setReverseDate(dubaiToday());
      setReverseIdempotencyKey(key);
      queryClient.invalidateQueries(["journalEntries"]);
    },
  });

  const resetPage = useCallback(() => {
    setPage(1);
    setCursors([undefined]);
  }, []);

  const resetKey = `${dateFrom?.getTime() ?? ""}|${dateTo?.getTime() ?? ""}|${
    glAccountCode ?? ""
  }|${entryType ?? ""}|${sortBy}|${sortDir}`;
  const [committedResetKey, setCommittedResetKey] = useState(resetKey);
  if (resetKey !== committedResetKey) {
    setCommittedResetKey(resetKey);
    resetPage();
  }

  const onRangeChange = (start: Date | null, end: Date | null) => {
    if (start && end && exceedsOneMonth(start, end)) {
      setDateError(DATE_RANGE_ERROR);
      return;
    }
    setDateError(undefined);
    setDateFrom(start);
    setDateTo(end);
  };

  const onSort = useCallback(
    (field: JournalEntrySortField) => {
      if (sortBy === field) {
        setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(field);
        setSortDir("desc");
      }
    },
    [sortBy]
  );

  const rangeTooLong = Boolean(
    dateFrom && dateTo && exceedsOneMonth(dateFrom, dateTo)
  );
  const shownDateError = rangeTooLong ? DATE_RANGE_ERROR : dateError;

  const searchQuery = useMemo<JournalEntrySearchQuery | undefined>(() => {
    if (!dateFrom || !dateTo || exceedsOneMonth(dateFrom, dateTo)) {
      return undefined;
    }
    return {
      start: journalDayStartIso(dateFrom),
      end: journalDayEndIso(dateTo),
      ...(glAccountCode ? { glAccountCode } : {}),
      ...(entryType ? { entryType } : {}),
      sortBy,
      sortDir,
    };
  }, [dateFrom, dateTo, entryType, glAccountCode, sortBy, sortDir]);

  const cursor = cursors[page - 1];
  const { isLoading, error, data } = useQuery<JournalEntryResult, Error>(
    ["journalEntries", page, searchQuery, cursor],
    () =>
      getJournalEntries({
        pageSize: PAGE_SIZE,
        searchQuery: searchQuery!,
        cursor,
      }),
    { enabled: Boolean(searchQuery) && (page === 1 || Boolean(cursor)) }
  );

  useEffect(() => {
    if (data?.glAccounts.length) setGlAccounts(data.glAccounts);
  }, [data]);

  useEffect(() => {
    if (!data?.hasMore || data.items.length === 0) return;
    const next = cursorFor(data.items[data.items.length - 1], sortBy);
    setCursors((current) => {
      const existing = current[page];
      if (existing?.id === next.id && existing.sortValue === next.sortValue) {
        return current;
      }
      const updated = current.slice();
      updated[page] = next;
      return updated;
    });
  }, [data, page, sortBy]);

  const goToPage = (next: number) => {
    if (next < 1) return;
    if (next > 1 && !cursors[next - 1]) return;
    setPage(next);
  };

  const onReverse = (item: JournalEntryItem) => {
    setNotice(undefined);
    const today = dubaiToday();
    const key = createIdempotencyKey();
    reverseKeyDate.current = today;
    setReverseDate(today);
    setReverseIdempotencyKey(key);
    setReverseTarget(item);
    reverseMutation.reset();
  };

  const onConfirmReverse = (event: FormEvent) => {
    event.preventDefault();
    if (!reverseTarget || !reverseDate) return;
    reverseMutation.mutate({
      transactionId: reverseTarget.transactionId,
      entryDate: reverseDate,
      idempotencyKey: reverseIdempotencyKey,
    });
  };

  const pageCount = data?.hasMore ? page + 1 : Math.max(page, 1);
  const totalItems = (page - 1) * PAGE_SIZE + (data?.items.length ?? 0);
  const glLabel =
    glAccounts.find((account) => account.code === glAccountCode)?.label ??
    "All GL accounts";
  const typeLabel =
    ENTRY_TYPES.find((option) => option.value === entryType)?.label ??
    "All types";

  return (
    <div>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Journal Entries</h1>
        <button
          type="button"
          className="btn btn-primary"
          data-testid="journal-entry-toggle"
          onClick={() => setCreating(true)}
        >
          Add journal entry
        </button>
      </div>
      <JournalEntryForm
        accounts={glAccounts}
        show={creating}
        onClose={() => setCreating(false)}
      />
      <Col>
        <div className={styles.filterRow}>
          <DateRangePicker
            startDate={dateFrom}
            endDate={dateTo}
            onChange={onRangeChange}
            placeholderText="Select date range"
          />
          <Dropdown className="add-filters-dropdown m-0">
            <Dropdown.Toggle
              variant="primary"
              className="search-toggle"
              data-testid="journal-entries-gl-filter"
            >
              <span>{glLabel}</span>
              <span className="material-icons material-icons-outlined">
                chevron_right
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                data-testid="journal-entries-gl-all"
                onClick={() => setGlAccountCode(undefined)}
              >
                All GL accounts
              </Dropdown.Item>
              {glAccounts.map((account) => (
                <Dropdown.Item
                  key={account.code}
                  data-testid={`journal-entries-gl-${account.code}`}
                  onClick={() => setGlAccountCode(account.code)}
                >
                  {account.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown className="add-filters-dropdown m-0">
            <Dropdown.Toggle
              variant="primary"
              className="search-toggle"
              data-testid="journal-entries-type-filter"
            >
              <span>{typeLabel}</span>
              <span className="material-icons material-icons-outlined">
                chevron_right
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                data-testid="journal-entries-type-all"
                onClick={() => setEntryType(undefined)}
              >
                All types
              </Dropdown.Item>
              {ENTRY_TYPES.map((option) => (
                <Dropdown.Item
                  key={option.value}
                  data-testid={`journal-entries-type-${option.value}`}
                  onClick={() => setEntryType(option.value)}
                >
                  {option.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <Modal
          show={Boolean(reverseTarget)}
          onHide={() => {
            if (!reverseMutation.isLoading) setReverseTarget(undefined);
          }}
          centered
          backdrop={reverseMutation.isLoading ? "static" : true}
          keyboard={!reverseMutation.isLoading}
          aria-labelledby="journal-reverse-modal-title"
        >
          <Modal.Header closeButton>
            <Modal.Title id="journal-reverse-modal-title">
              Reverse journal entry
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form
              className={styles.reverseForm}
              onSubmit={onConfirmReverse}
              data-testid="journal-entries-reverse-form"
            >
              <p>
                Reverse every line in transaction {reverseTarget?.transactionId}
                . The new lines use the posting date below.
              </p>
              <label className={styles.reverseField}>
                Posting date
                <input
                  data-testid="journal-entries-reverse-date"
                  type="date"
                  min={postingBounds.min}
                  max={postingBounds.max}
                  value={reverseDate}
                  onChange={(event) => {
                    const next = event.target.value;
                    setReverseDate(next);
                    if (next !== reverseKeyDate.current) {
                      reverseKeyDate.current = next;
                      setReverseIdempotencyKey(createIdempotencyKey());
                    }
                  }}
                />
              </label>
              {reverseMutation.isError && (
                <p
                  className={styles.dateError}
                  data-testid="journal-entries-reverse-error"
                >
                  {messageFrom(reverseMutation.error)}
                </p>
              )}
              <div className={styles.reverseActions}>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  data-testid="journal-entries-reverse-cancel"
                  onClick={() => setReverseTarget(undefined)}
                  disabled={reverseMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  data-testid="journal-entries-reverse-confirm"
                  disabled={reverseMutation.isLoading || !reverseDate}
                >
                  Confirm reversal
                </button>
              </div>
            </form>
          </Modal.Body>
        </Modal>
        {notice && <p data-testid="journal-entries-notice">{notice}</p>}
        {shownDateError && (
          <div
            className={styles.dateError}
            data-testid="journal-entries-date-error"
          >
            {shownDateError}
          </div>
        )}
        {data && (
          <div className="mt-3" data-testid="journal-entries-result-count">
            {data.items.length} {data.items.length === 1 ? "result" : "results"}{" "}
            on this page
          </div>
        )}
      </Col>
      <div className={styles.container}>
        {data && (
          <PaginationComponent
            pageCount={pageCount}
            currentPage={page}
            itemOfPage={data.items.length}
            totalItems={totalItems}
            containerClass={styles.tableTopPagination}
            setCurrentPage={goToPage}
          />
        )}
        <JournalEntriesTable
          data={data}
          error={error as Error | null}
          isLoading={isLoading}
          idleMessage={
            searchQuery
              ? undefined
              : rangeTooLong
              ? DATE_RANGE_ERROR
              : "Select a date range of at most one month."
          }
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={onSort}
          onReverse={onReverse}
          pendingTransactionId={
            reverseMutation.isLoading ? reverseTarget?.transactionId : undefined
          }
        />
        {data && (
          <PaginationComponent
            pageCount={pageCount}
            currentPage={page}
            itemOfPage={data.items.length}
            totalItems={totalItems}
            setCurrentPage={goToPage}
          />
        )}
      </div>
    </div>
  );
};

export default memo(JournalEntries);
